import {
  core_v1_DataSource,
  dbui_v1_CreateFunctionRequest,
} from '@antimatterhq/asl-api-ts-v1';
import { produce, WritableDraft } from 'immer';
import {
  getStreamTablesValues,
  setStreamTablesValues,
} from '../streamTable/streamTableHelper';
import { getFieldSchemaValues, setFieldSchemaValues } from './field';
import {
  getPreserveUnreferencedColumnsValues,
  setPreserveUnreferencedColumns,
} from './preserveUnreferencedColumns';
import { getJSONExtractValues, setJsonExtract } from './jsonExtractions';
import { deepEqual, filterStringArray } from './datasourceUtils';
import {
  generateNotebookFunctionName,
  getCustomFunctionValues,
  setCustomFunction,
} from './customFunction';
import { getTemporaryFields, setTemporaryFields } from './temporaryFields';
import { filterNulls } from '@/utils/filterNulls';
import { PreTransformSchemaFormValues } from '@/zod/schemas/dataSource/PreTransform';
import {
  Datasource_Preset,
  PreTransform_Preset,
  TransformRequest_Transform,
} from '@/types/api';
import { StreamTableSchemaValues } from '@/zod/schemas/StreamTableSchema';

export const getBronzeTablesValues = (
  dataSource: core_v1_DataSource,
): StreamTableSchemaValues[] => {
  const bronzeTables = dataSource.spec.silver?.bronzeTables ?? [];
  return getStreamTablesValues(
    bronzeTables.filter((q) => q.name !== dataSource.spec.bronze?.bronzeTable),
    { streaming: true, aliasRequired: true, joinHidden: false },
  );
};

export const getMethodValue = (dataSource: core_v1_DataSource) => {
  const { skipPreTransform = false, custom = undefined } =
    dataSource.spec.silver?.preTransform || {};

  if (skipPreTransform) {
    return 'none';
  }

  if (custom?.function) {
    return 'custom';
  }

  return 'configure';
};

export const getSilverPreTransformInitialValues = (
  dataSource: core_v1_DataSource,
): PreTransformSchemaFormValues => {
  const usePreset = dataSource.spec.silver?.preTransform?.usePreset || '';
  const omitFields =
    dataSource.spec.silver?.preTransform?.presetOverrides?.omitFields || [];
  const filter = dataSource.spec.silver?.preTransform?.filter || '';
  const postFilter = dataSource.spec.silver?.preTransform?.postFilter || '';
  const addFields = getFieldSchemaValues(
    filterNulls(dataSource.spec.silver?.preTransform?.addFields),
  );

  const bronzeTables = getBronzeTablesValues(dataSource);
  const preserveUnreferencedColumns = getPreserveUnreferencedColumnsValues(
    dataSource.spec.silver?.preTransform?.utils,
  );
  const jsonExtract = getJSONExtractValues(
    dataSource.spec.silver?.preTransform?.utils,
  );

  const method = getMethodValue(dataSource);
  const customNotebookPath = dataSource.spec.custom?.notebook ?? '';

  return {
    usePreset,
    omitFields,
    filter,
    postFilter,
    addFields,
    bronzeTables,
    jsonExtract,
    method,
    customNotebookPath,
    ...getCustomFunctionValues(dataSource.spec.silver?.preTransform?.custom),
    ...preserveUnreferencedColumns,
    ...getTemporaryFields(dataSource.spec.silver?.preTransform?.utils),
  };
};

export const getIsSilverPreTransformCompleted = (
  dataSource: core_v1_DataSource,
): boolean => {
  return !!dataSource;
};

export const setSilverPreTransformFormValues = (
  dataSource: core_v1_DataSource,
  values: PreTransformSchemaFormValues,
): core_v1_DataSource => {
  const bronzeTable = dataSource.spec.silver?.bronzeTables?.find(
    (q) => q.name === dataSource.spec.bronze?.bronzeTable,
  );
  if (!bronzeTable) {
    throw new Error(
      `Bronze table missing: "${dataSource.spec.bronze?.bronzeTable}"`,
    );
  }
  const bronzeTables = [
    bronzeTable,
    ...values.bronzeTables.map(setStreamTablesValues),
  ];
  const omitPresetFields = filterStringArray(values.omitFields);
  const addFields = setFieldSchemaValues(values.addFields);
  const configure = values.method === 'configure';
  const custom = values.method === 'custom';
  const notebook = values.customNotebookPath;
  return produce(dataSource, (draftState) => ({
    ...draftState,
    spec: {
      ...draftState.spec,
      custom: notebook
        ? {
            notebook,
          }
        : undefined,
      silver: {
        ...draftState.spec.silver,
        preTransform: {
          usePreset:
            configure && values.usePreset !== 'none'
              ? values.usePreset || undefined
              : undefined,
          skipPreTransform: values.method === 'none',
          presetOverrides:
            configure && omitPresetFields?.length
              ? {
                  omitFields: filterStringArray(values.omitFields),
                }
              : undefined,
          filter: configure ? values.filter || undefined : undefined,
          postFilter: configure ? values.postFilter || undefined : undefined,
          addFields: configure && addFields?.length ? addFields : undefined,
          utils: configure
            ? {
                unreferencedColumns: setPreserveUnreferencedColumns(values),
                jsonExtract: setJsonExtract(values.jsonExtract),
                temporaryFields: setTemporaryFields(values),
              }
            : undefined,
          custom: custom ? setCustomFunction(values) : undefined,
        },
        bronzeTables: bronzeTables.length ? bronzeTables : undefined,
      },
    },
  }));
};

export const setPreTransformCustomFunctionRequest = (
  datasource: core_v1_DataSource,
  values: PreTransformSchemaFormValues,
) => {
  const notebook = values.customNotebookPath;
  if (!notebook) {
    throw new Error(
      'Unable to create custom function, notebook or name not found',
    );
  }

  const name = generateNotebookFunctionName(
    datasource.metadata?.name || '',
    datasource.spec.bronze?.bronzeTable || '',
  );
  const result: dbui_v1_CreateFunctionRequest = {
    name,
    path: notebook,
    preFilter: values.filter || undefined,
    postFilter: values.postFilter || undefined,
    fields: setFieldSchemaValues(values.addFields),
    utils: {
      unreferencedColumns: setPreserveUnreferencedColumns(values),
      jsonExtract: setJsonExtract(values.jsonExtract),
    },
    templateType: 'datasource',
  };

  return result;
};

export const applyPreTransformPreset = (
  values: PreTransformSchemaFormValues,
  preset: PreTransform_Preset | undefined,
  usePreset: string,
): PreTransformSchemaFormValues => {
  const embedColumn = preset?.utils?.unreferencedColumns?.embedColumn || '';
  const omitColumns = preset?.utils?.unreferencedColumns?.omitColumns || [];

  return {
    ...values,
    usePreset,
    filter: preset?.filter || '',
    postFilter: preset?.postFilter || '',
    preserve: preset?.utils?.unreferencedColumns?.preserve || false,
    duplicatePrefix: preset?.utils?.unreferencedColumns?.duplicatePrefix || '',
    embedColumn,
    omitColumns,
    embed: !!embedColumn || !!omitColumns.length,
    jsonExtract:
      preset?.utils?.jsonExtract?.map((extract) => ({
        source: extract.source || '',
        embed: !!extract.embedColumn,
        embedColumn: extract.embedColumn || '',
        omitFields: extract.omitFields || [],
        duplicatePrefix: '',
      })) || [],
  };
};

export const upgradePreTransformPreset = (
  datasource: core_v1_DataSource,
  preset: Datasource_Preset,
) => {
  const preTransform = datasource.spec.silver?.preTransform;
  if (!preTransform) {
    return datasource;
  }

  const preTransformPreset = preset.silver?.preTransform?.find(
    (p) => p.name === preTransform.usePreset,
  );

  if (!preTransformPreset) {
    return datasource;
  }

  return produce(datasource, (draftState) => {
    draftState.spec.silver = {
      ...draftState.spec.silver,
      preTransform: {
        filter:
          preTransformPreset.filter ||
          draftState.spec.silver?.preTransform?.filter ||
          undefined,
        postFilter:
          preTransformPreset.postFilter ||
          draftState.spec.silver?.preTransform?.postFilter ||
          undefined,
        addFields: draftState.spec.silver?.preTransform?.addFields || undefined,
        utils: {
          unreferencedColumns: {
            embedColumn:
              preTransformPreset.utils?.unreferencedColumns?.embedColumn ||
              draftState.spec.silver?.preTransform?.utils?.unreferencedColumns
                ?.embedColumn ||
              undefined,
            embedColumnType:
              preTransformPreset.utils?.unreferencedColumns?.embedColumnType ||
              draftState.spec.silver?.preTransform?.utils?.unreferencedColumns
                ?.embedColumnType ||
              undefined,
            duplicatePrefix:
              preTransformPreset.utils?.unreferencedColumns?.duplicatePrefix ||
              draftState.spec.silver?.preTransform?.utils?.unreferencedColumns
                ?.duplicatePrefix ||
              undefined,
            omitColumns:
              preTransformPreset.utils?.unreferencedColumns?.omitColumns ||
              draftState.spec.silver?.preTransform?.utils?.unreferencedColumns
                ?.omitColumns ||
              undefined,
            preserve:
              preTransformPreset.utils?.unreferencedColumns?.preserve ||
              draftState.spec.silver?.preTransform?.utils?.unreferencedColumns
                ?.preserve ||
              undefined,
          },
          temporaryFields:
            preTransformPreset.utils?.temporaryFields ||
            draftState.spec.silver?.preTransform?.utils?.temporaryFields ||
            undefined,
        },
      },
    };
  });
};

export const setSkipPresets = (dataSource: core_v1_DataSource) => {
  return produce(
    dataSource,
    (draftState: WritableDraft<core_v1_DataSource>) => {
      draftState.spec.silver = {
        ...draftState.spec.silver,
        preTransform: {
          skipPreTransform: true,
        },
      };
    },
  );
};

export const preTransformHasUnsavedChanges = (
  datasource: core_v1_DataSource,
  unsavedDatasource: core_v1_DataSource | undefined,
) => {
  if (!unsavedDatasource) {
    return false;
  }
  return (
    !deepEqual(
      datasource.spec.silver?.preTransform,
      unsavedDatasource.spec.silver?.preTransform,
    ) ||
    !deepEqual(
      datasource.spec.silver?.bronzeTables,
      unsavedDatasource.spec.silver?.bronzeTables,
    )
  );
};

export const mapPreTransformToTransformRequest = (
  datasource: core_v1_DataSource,
): TransformRequest_Transform => {
  const preTransform = datasource.spec?.silver?.preTransform;
  return {
    transformType: 'SilverPreTransform',
    filter: preTransform?.filter || undefined,
    postFilter: preTransform?.postFilter || undefined,
    utils: preTransform?.utils || undefined,
    addFields: preTransform?.addFields || undefined,
    presetOverrides: preTransform?.presetOverrides?.omitFields
      ? { omitFields: preTransform?.presetOverrides?.omitFields }
      : undefined,
  };
};
